import { Injectable, OnDestroy } from '@angular/core';
import { Subject, BehaviorSubject, fromEvent } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import {
  BOT_EXTRACT_PERMISSION_NAMES,
  MAIN_PERMISSIONS,
  ROLES,
  SUB_PERMISSIONS,
} from '../data/config/constants';

// Menu
export interface Menu {
  headTitle1?: string;
  headTitle2?: string;
  path?: string;
  title?: string;
  icon?: string;
  type?: string;
  badgeType?: string;
  badgeValue?: string;
  active?: boolean;
  bookmark?: boolean;
  children?: Menu[];
  mainPermissionId?: number | string | Array<number | string>;
  subPermissionsIds?: number | string | Array<number | string>;
  requiresAllPermissions?: boolean;
  allowedRoleIds?: number[];
}

@Injectable({
  providedIn: 'root',
})
export class NavService implements OnDestroy {
  private unsubscriber: Subject<any> = new Subject();
  public screenWidth: BehaviorSubject<number> = new BehaviorSubject(
    window.innerWidth,
  );

  // Search Box
  public search: boolean = false;

  // Language
  public language: boolean = false;

  // Mega Menu
  public megaMenu: boolean = false;
  public levelMenu: boolean = false;
  public megaMenuColapse: boolean = window.innerWidth < 1199 ? true : false;

  // Collapse Sidebar
  public collapseSidebar: boolean = window.innerWidth < 991 ? true : false;

  // For Horizontal Layout Mobile
  public horizontal: boolean = window.innerWidth < 991 ? false : true;

  // Full screen
  public fullScreen: boolean = false;

  // Permisos principales
  public MAIN_PERMISSIONS = MAIN_PERMISSIONS;
  public SUB_PERMISSIONS = SUB_PERMISSIONS;

  constructor(private router: Router) {
    this.setScreenWidth(window.innerWidth);
    fromEvent(window, 'resize')
      .pipe(debounceTime(1000), takeUntil(this.unsubscriber))
      .subscribe((evt: any) => {
        this.setScreenWidth(evt.target.innerWidth);
        if (evt.target.innerWidth < 991) {
          this.collapseSidebar = true;
          this.megaMenu = false;
          this.levelMenu = false;
        }
        if (evt.target.innerWidth < 1199) {
          this.megaMenuColapse = true;
        }
      });
    if (window.innerWidth < 991) {
      // Detect Route change sidebar close
      this.router.events.subscribe((event) => {
        this.collapseSidebar = true;
        this.megaMenu = false;
        this.levelMenu = false;
      });
    }
  }

  ngOnDestroy() {
    this.unsubscriber.complete();
  }

  private setScreenWidth(width: number): void {
    this.screenWidth.next(width);
  }

  MENUITEMS: Menu[] = [
    {
      headTitle1: 'General',
      mainPermissionId: [MAIN_PERMISSIONS.Dashboard, MAIN_PERMISSIONS.Perfil],
    },
    {
      mainPermissionId: MAIN_PERMISSIONS.Dashboard,
      title: 'Dashboard',
      icon: 'home',
      type: 'link',
      badgeType: 'light-primary',
      path: '/dashboard/default',
    },
    {
      mainPermissionId: [MAIN_PERMISSIONS.Dashboard, MAIN_PERMISSIONS.Perfil],
      requiresAllPermissions: true,
      allowedRoleIds: [ROLES.AGENT, ROLES.AFFILIATE],
      title: 'Perfil',
      icon: 'user',
      type: 'link',
      badgeType: 'light-primary',
      path: '/user/profile',
    },
    {
      title: 'Suppliers',
      icon: 'button',
      type: 'link',
      badgeType: 'light-primary',
      path: '/suppliers/all-suppliers',
    },
    {
      title: 'Products',
      icon: 'ecommerce',
      type: 'link',
      badgeType: 'light-primary',
      path: '/products/all-products',
    },
    {
      headTitle1: 'Users',
      mainPermissionId: MAIN_PERMISSIONS.Usuarios,
    },
    {
      mainPermissionId: MAIN_PERMISSIONS.Usuarios,
      title: 'Users',
      icon: 'user',
      type: 'sub',
      badgeType: 'light-secondary',
      active: false,
      children: [
        {
          title: 'Agents',
          type: 'sub',
          badgeType: 'light-secondary',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Agentes],
          children: [
            {
              path: '/users/agents',
              title: 'Agent List',
              type: 'link',
              subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Agentes],
            },
            {
              path: '/users/agents/verified-agents',
              title: 'Verified Agents',
              type: 'link',
              subPermissionsIds: [
                SUB_PERMISSIONS.Sub_Modulo_Agentes_Verificados,
              ],
            },
            {
              path: '/users/agents/agency-application',
              title: 'Agency Application',
              type: 'link',
              subPermissionsIds: [SUB_PERMISSIONS.Sub_modulo_solicitud_Agencia],
            },
          ],
        },
        {
          path: '/users/holders',
          title: 'Holders',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Titulares],
        },
        {
          path: '/users/administratives',
          title: 'Administratives',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Administrativos],
        },
        {
          path: '/users/players',
          title: `Players`,
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Jugadores],
        },
      ],
    },
    {
      mainPermissionId: MAIN_PERMISSIONS.RRHH,
      title: 'RRHH',
      icon: 'user',
      type: 'sub',
      badgeType: 'light-secondary',
      active: false,
      children: [
        {
          path: '/rrhh/payroll',
          title: 'Nominas',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Nominas],
        },
        {
          path: '/rrhh/permit',
          title: 'Permit',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Permisos_Empleados],
        },
      ],
    },
    {
      headTitle1: 'Accounting and Finance',
      mainPermissionId: MAIN_PERMISSIONS.Contabilidad,
    },
    {
      mainPermissionId: MAIN_PERMISSIONS.Contabilidad,
      title: 'Accounting',
      icon: 'charts',
      type: 'sub',
      badgeType: 'light-secondary',
      active: false,
      children: [
        {
          path: '/accounting/account/',
          title: 'Account',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Cuentas],
        },
        {
          path: '/accounting/financial',
          title: 'Financial',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Financiero],
        },
        {
          path: '/accounting/accounting-accounts',
          title: 'Accounting Accounts',
          type: 'link',
        },
        {
          title: 'Accounting docs',
          type: 'sub',
          badgeType: 'light-secondary',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Documentos_Contables],
          children: [
            {
              path: '/accounting/diary',
              title: 'Diary',
              type: 'link',
              subPermissionsIds: [
                SUB_PERMISSIONS.Sub_Modulo_Documentos_Contables_Diario,
              ],
            },
            { path: '/accounting/major', title: 'Major', type: 'link' },
          ],
        },
        {
          path: '/accounting/financial-statement',
          title: 'Financial Statement',
          type: 'link',
        },
        {
          path: '/accounting/result-statement',
          title: 'Result Statement',
          type: 'link',
        },
        { path: '/accounting/bank-report', title: 'Bank Report', type: 'link' },
        { path: '/accounting/banking', title: 'Banking', type: 'link' },
        { path: '/accounting/financial', title: 'Financial', type: 'link' },
        {
          title: 'Banking',
          type: 'sub',
          badgeType: 'light-secondary',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Bancario],
          children: [
            {
              path: '/accounting/banking/bank-report',
              title: 'Bank Report',
              type: 'link',
              subPermissionsIds: [
                SUB_PERMISSIONS.Sub_Modulo_Bancario_Reporte_Bancario,
              ],
            },
            {
              path: '/accounting/banking/bank-accounts',
              title: 'Accounts Banks',
              type: 'link',
              subPermissionsIds: [
                SUB_PERMISSIONS.Sub_Modulo_Bancario_Cuentas_Bancarias,
              ],
            },
            {
              path: '/accounting/bank',
              title: 'Bancos',
              type: 'link',
              subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Bancario_Bancos],
            },
          ],
        },
        {
          path: '/accounting/accounting-accounts',
          title: 'Accounting Accounts',
          type: 'link',
        },
        {
          path: '/accounting/diary',
          title: 'Diary',
          type: 'link',
        },
        {
          path: '/accounting/major',
          title: 'Major',
          type: 'link',
        },
        {
          path: '/accounting/financial-statement',
          title: 'Financial Statement',
          type: 'link',
        },
        {
          path: '/accounting/result-statement',
          title: 'Result Statement',
          type: 'link',
        },
        {
          path: '/accounting/bank-report',
          title: 'Bank Report',
          type: 'link',
        },
        {
          path: '/accounting/banking',
          title: 'Banking',
          type: 'link',
        },
      ],
    },
    {
      title: 'Billing',
      icon: 'sample-page',
      type: 'sub',
      badgeType: 'light-secondary',
      active: false,
      children: [
        { path: '/billing/bill-entry', title: 'Bill Entry', type: 'link' },
        { path: '/billing/bill-invoice', title: 'Bill Invoice', type: 'link' },
        { path: '/billing/credit-note', title: 'Credit Note', type: 'link' },
        {
          path: '/billing/referral-guide',
          title: 'Referral Guide',
          type: 'link',
        },
        {
          path: '/billing/proof-withholding',
          title: 'Proof Withholding',
          type: 'link',
        },
        {
          path: '/billing/liquidation-purchase-assets-provision-service',
          title: 'Liquidation of purchase of goods and provision of service',
          type: 'link',
        },
      ],
    },
    {
      headTitle1: 'Transactions',
      mainPermissionId: [
        MAIN_PERMISSIONS['Abonos y Pagos'],
        MAIN_PERMISSIONS['Cargas y Retiros'],
      ],
    },
    {
      mainPermissionId: MAIN_PERMISSIONS['Cargas y Retiros'],
      title: 'Loads and Withdrawals',
      icon: 'social',
      type: 'sub',
      badgeType: 'light-secondary',
      active: false,
      children: [
        {
          path: '/loads-withdrawals/list-lw',
          title: 'List',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Lista_CR],
        },
      ],
    },
    {
      mainPermissionId: MAIN_PERMISSIONS['Abonos y Pagos'],
      title: 'Subscriptions and Payments',
      icon: 'others',
      type: 'sub',
      badgeType: 'light-secondary',
      active: false,
      children: [
        {
          path: '/suscriptions-payments/list-sp',
          title: 'List',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Lista_AP],
        },
        {
          path: '/suscriptions-payments/bank-transaction-bot',
          title: 'Transaction Bot',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Transaccion_Banco],
        },
        {
          path: '/suscriptions-payments/request-cw',
          title: 'Request Loading or Withdrawal',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Solicitar_CR],
        },
      ],
    },
    {
      mainPermissionId: MAIN_PERMISSIONS.Bonos,
      title: 'Bonus',
      icon: 'bonus-kit',
      type: 'sub',
      badgeType: 'light-secondary',
      active: false,
      children: [
        {
          path: '/bonus/bonus-type-management',
          title: 'Gestión de Bonos',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Gestion_Bonos],
        },
        {
          path: '/bonus/balance-bonus',
          title: 'Balance Bonus',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Bono_Saldos],
        },
        {
          path: '/bonus/bonus-code',
          title: 'Bonus Code',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Codigo_Bonos],
          type: 'link',
        },
      ],
    },
    {
      mainPermissionId: MAIN_PERMISSIONS.Balance,
      title: 'Balance de página',
      icon: 'search',
      type: 'link',
      badgeType: 'light-secondary',
      path: '/balance/page-balance',
    },
    {
      mainPermissionId: MAIN_PERMISSIONS.Debt,
      title: 'List of debts',
      icon: 'search',
      type: 'sub',
      badgeType: 'light-secondary',
      active: false,
      children: [
        {
          path: '/debt-list/list',
          title: 'Debt Adjustment',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Lista_Deuda],
          type: 'link',
        },
      ],
    },
    {
      mainPermissionId: MAIN_PERMISSIONS.Marketing,
      headTitle1: 'Affiliate Tools',
    },
    {
      subPermissionsIds: [SUB_PERMISSIONS.Sub_Module_Marketing],
      title: 'Marketing',
      icon: 'user',
      type: 'link',
      badgeType: 'light-primary',
      path: '/affiliate/marketing',
    },
    {
      subPermissionsIds: [SUB_PERMISSIONS.Sub_Module_Promotional_Material],
      title: 'Material Promocional',
      icon: 'file',
      type: 'link',
      badgeType: 'light-primary',
      path: '/affiliate/promotional-material',
    },
    {
      subPermissionsIds: [SUB_PERMISSIONS.Sub_Module_Referrals_Link],
      title: 'Enlace de Referencia',
      icon: 'social',
      type: 'link',
      badgeType: 'light-primary',
      path: '/affiliate/referrals-link',
    },
    {
      mainPermissionId: MAIN_PERMISSIONS.Marketing,
      subPermissionsIds: [SUB_PERMISSIONS.Sub_Module_Prediction],
      title: 'Prediction',
      icon: 'file',
      type: 'sub',
      badgeType: 'light-primary',
      active: false,
      children: [
        {
          path: '/affiliate/template',
          title: 'Template',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Plantilla],
        },
        {
          path: '/affiliate/questions',
          title: 'Questions',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Pregunta],
        },
        {
          path: '/affiliate/options',
          title: 'Options',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Opciones],
        },
        {
          path: '/affiliate/report',
          title: 'Reports',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Reporte],
        },
      ],
    },
    {
      subPermissionsIds: [SUB_PERMISSIONS.Sub_Module_Insert_Feedback],
      title: 'Send Feedback',
      icon: 'email',
      type: 'link',
      badgeType: 'light-primary',
      path: '/feedback/send-feedback',
    },
    {
      subPermissionsIds: [SUB_PERMISSIONS.Sub_Module_Manage_Feedback],
      title: 'Manage Feedbacks',
      icon: 'email',
      type: 'link',
      badgeType: 'light-primary',
      path: '/feedback/manage-feedback',
    },
    {
      mainPermissionId: [
        MAIN_PERMISSIONS.Bot,
        BOT_EXTRACT_PERMISSION_NAMES.ViewModule,
      ],
      headTitle1: 'Bot Tools',
    },
    {
      mainPermissionId: [
        MAIN_PERMISSIONS.Bot,
        BOT_EXTRACT_PERMISSION_NAMES.ViewModule,
      ],
      title: 'Bot',
      icon: 'bonus-kit',
      type: 'sub',
      badgeType: 'light-secondary',
      active: false,
      children: [
        {
          path: '/bot/actions-menu',
          title: 'Actions',
          type: 'link',
          subPermissionsIds: [
            SUB_PERMISSIONS.Sub_Modulo_Bot,
            BOT_EXTRACT_PERMISSION_NAMES.ViewModule,
          ],
        },
      ],
    },
    {
      headTitle1: 'Management tools',
      subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Auditoria],
    },
    {
      title: 'Audit',
      subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Auditoria],
      icon: 'search',
      type: 'link',
      badgeType: 'light-primary',
      path: '/audit',
    },
    {
      title: 'Configuración de Auditoría',
      subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Configuracion_Auditoria],
      icon: 'form',
      type: 'link',
      badgeType: 'light-primary',
      path: '/audit-config',
    },
    {
      mainPermissionId: MAIN_PERMISSIONS['Roles y Permisos'],
      title: 'Roles and Permissions',
      icon: 'user',
      type: 'sub',
      badgeType: 'light-secondary',
      active: false,
      children: [
        {
          path: '/roles-and-permissions/modules',
          title: 'Modules and Permission',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Consultar_Modulos_Permisos],
        },
        {
          path: '/roles-and-permissions/roles',
          title: 'Roles and Modules',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Roles],
        },
      ],
    },
    {
      subPermissionsIds: [SUB_PERMISSIONS.Sub_Module_Send_Support_Zgames_Ticket],
      title: 'Soportes ZGames',
      icon: 'support-tickets',
      type: 'link',
      badgeType: 'light-primary',
      path: '/support-zgames/send-ticket',
    },
    {
      headTitle1: 'Reports',
      mainPermissionId: MAIN_PERMISSIONS.Reporte,
    },
    {
      title: 'Reports',
      icon: 'social',
      type: 'sub',
      mainPermissionId: MAIN_PERMISSIONS.Reporte,
      badgeType: 'light-secondary',
      active: false,
      children: [
        {
          path: '/closure/closure-manager',
          title: 'Closure Manager',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Gestion_cierre],
        },
        {
          path: '/closure',
          title: 'Closure',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Cierre],
        },
        {
          path: '/closure/daily',
          title: 'Daily',
          type: 'link',
          subPermissionsIds: [SUB_PERMISSIONS.Sub_Modulo_Diario],
        },
      ],
    },
  ];

  // Array
  items = new BehaviorSubject<Menu[]>(this.MENUITEMS);

  private normalizePermissions(
    permissions?: number | string | Array<number | string>,
  ): Array<number | string> {
    if (permissions === null || permissions === undefined) {
      return [];
    }

    return Array.isArray(permissions) ? permissions : [permissions];
  }

  private buildPermissionSets(rol: any) {
    const mainPermissions = rol?.permisos_principales || [];

    return {
      mainPermissionIds: new Set<number>(
        mainPermissions.map((permission: { id: number }) => permission.id),
      ),
      subPermissionIds: new Set<number>(
        mainPermissions.flatMap((permission: { permisos?: any[] }) =>
          (permission.permisos || []).map(
            (subPermission: { id: number }) => subPermission.id,
          ),
        ),
      ),
      mainPermissionNames: new Set<string>(
        mainPermissions
          .map(
            (permission: { nombre_permiso?: string }) =>
              permission.nombre_permiso,
          )
          .filter(
            (permissionName: string | undefined): permissionName is string =>
              Boolean(permissionName),
          ),
      ),
      subPermissionNames: new Set<string>(
        mainPermissions.flatMap((permission: { permisos?: any[] }) =>
          (permission.permisos || [])
            .map(
              (subPermission: { nombre_permiso?: string }) =>
                subPermission.nombre_permiso,
            )
            .filter(
              (permissionName: string | undefined): permissionName is string =>
                Boolean(permissionName),
            ),
        ),
      ),
    };
  }

  private matchesPermission(
    permission: number | string,
    permissionSets: {
      mainPermissionIds: Set<number>;
      subPermissionIds: Set<number>;
      mainPermissionNames: Set<string>;
      subPermissionNames: Set<string>;
    },
  ): boolean {
    if (typeof permission === 'number') {
      return (
        permissionSets.mainPermissionIds.has(permission) ||
        permissionSets.subPermissionIds.has(permission)
      );
    }

    return (
      permissionSets.mainPermissionNames.has(permission) ||
      permissionSets.subPermissionNames.has(permission)
    );
  }

  private routeHasPermission(
    route: Menu,
    permissionSets: {
      mainPermissionIds: Set<number>;
      subPermissionIds: Set<number>;
      mainPermissionNames: Set<string>;
      subPermissionNames: Set<string>;
    },
    roleId?: number,
  ): boolean {
    if (route.allowedRoleIds?.length && !route.allowedRoleIds.includes(roleId || 0)) {
      return false;
    }

    const mainPermissionIds = this.normalizePermissions(route.mainPermissionId);
    const subPermissionIds = this.normalizePermissions(route.subPermissionsIds);
    const requiresAllPermissions = route.requiresAllPermissions || false;

    if (!mainPermissionIds.length && !subPermissionIds.length) {
      return false;
    }

    if (requiresAllPermissions) {
      return (
        mainPermissionIds.every((permission: number | string) =>
          this.matchesPermission(permission, permissionSets),
        ) &&
        subPermissionIds.every((permission: number | string) =>
          this.matchesPermission(permission, permissionSets),
        )
      );
    }

    return (
      mainPermissionIds.some((permission: number | string) =>
        this.matchesPermission(permission, permissionSets),
      ) ||
      subPermissionIds.some((permission: number | string) =>
        this.matchesPermission(permission, permissionSets),
      )
    );
  }

  private removeEmptySectionHeaders(routes: Menu[]): Menu[] {
    return routes.filter((route, index) => {
      if (!route.headTitle1 && !route.headTitle2) {
        return true;
      }

      const nextHeaderIndex = routes.findIndex(
        (item, itemIndex) =>
          itemIndex > index && (item.headTitle1 || item.headTitle2),
      );

      const sectionItems =
        nextHeaderIndex === -1
          ? routes.slice(index + 1)
          : routes.slice(index + 1, nextHeaderIndex);

      return sectionItems.some((item) => !item.headTitle1 && !item.headTitle2);
    });
  }

  // Metodo para filtrar items de menu basado en permisos del usuario
  filterRoutesByPermissions = (routes: any, rol: any) => {
    const permissionSets = this.buildPermissionSets(rol);
    const roleId = rol?.id;

    const filterRoutes = (items: Menu[]): Menu[] => {
      const filteredRoutes = items.reduce((acc: Menu[], route: Menu) => {
        const filteredChildren = route.children
          ? filterRoutes(route.children)
          : undefined;

        const hasOwnPermission = this.routeHasPermission(
          route,
          permissionSets,
          roleId,
        );
        const hadChildren = Boolean(route.children?.length);
        const hasVisibleChildren = Boolean(filteredChildren?.length);
        const isSectionHeader = Boolean(route.headTitle1 || route.headTitle2);

        let shouldInclude = hasOwnPermission || hasVisibleChildren;

        // Evita mostrar modulos desplegables vacios aunque el permiso principal exista.
        if (hadChildren && route.type === 'sub') {
          shouldInclude = hasVisibleChildren;
        }

        if (isSectionHeader) {
          shouldInclude = hasOwnPermission;
        }

        if (!shouldInclude) {
          return acc;
        }

        acc.push({
          ...route,
          children: filteredChildren,
        });

        return acc;
      }, []);

      return this.removeEmptySectionHeaders(filteredRoutes);
    };

    return filterRoutes(routes);
  };

  // Método para actualizar los ítems del menú basado en permisos del usuario
  updateMenuItems(rol: any) {
    const permisosPrincipales = (rol?.permisos_principales || []).map(
      (permission: any) => ({
        id: permission.id,
        nombre_permiso: permission.nombre_permiso,
        permisos: (permission.permisos || []).map((subPermission: any) => ({
          id: subPermission.id,
          nombre_permiso: subPermission.nombre_permiso,
        })),
      }),
    );

    const transformedRole = {
      permisos_principales: permisosPrincipales,
    };

    const filteredMenuItems = this.filterRoutesByPermissions(
      this.MENUITEMS,
      transformedRole,
    );
    this.items.next(filteredMenuItems);
  }
}
